import React, { useEffect, useState } from 'react';
import { useMoney } from "../../hooks/useMoney";
import { Loader2, RefreshCw, CheckCircle } from 'lucide-react';
import CustomModal from '../common/CustomModal';
import type { DistributorTransaction } from '../../features/distributor/DistributorSlice';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { recalculateTransactions, fetchDistributorTransaction } from '../../features/distributor/DistributorSlice';

interface DistributorTransactionModalProps {
  show: boolean;
  onHide: () => void;
  transactions: DistributorTransaction[] | null;
  loading: boolean;
  error: string | null;
  programName: string;
  walletId: number | null;
  distributorId: number | null;
  programId: number | null;
  onRefreshTransactions?: () => void;
}

const DistributorTransactionModal: React.FC<DistributorTransactionModalProps> = ({
  show,
  onHide,
  transactions,
  loading,
  error,
  programName,
  walletId,
  distributorId,
  programId,
  onRefreshTransactions,
}) => {
  const { symbol, locale } = useMoney();
  const dispatch = useAppDispatch();
  const {
    recalculatingTransactions,
    recalculateTransactionsError,
    recalculateTransactionsSuccess,
  } = useAppSelector((state) => state.distributor);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleRefreshClick = async () => {
    if (!walletId) return;

    const result = await dispatch(
      recalculateTransactions({
        walletId,
      })
    );

    if (recalculateTransactions.fulfilled.match(result)) {
      setShowSuccessMessage(true);
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);

      // Auto-refresh transactions
      if (onRefreshTransactions) {
        onRefreshTransactions();
      } else if (distributorId && programId) {
        dispatch(
          fetchDistributorTransaction({
            distributorId,
            programId: programId.toString(),
            page: 1,
            limit: 1000,
            sortBy: 'createdAt',
            sortOrder: 'DESC',
            forModal: true,
          })
        );
      }
    }
  };

  // Reset success message when modal is closed
  useEffect(() => {
    if (!show) {
      setShowSuccessMessage(false);
    }
  }, [show]);
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (value: string | number | undefined) => {
    if (value === undefined || value === null) return `${symbol}0`;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${symbol}${num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatOptionalAmount = (value: string | number | undefined | null) => {
    if (value === undefined || value === null) return '-';
    return formatAmount(value);
  };

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      title={
        <div className="flex items-center justify-between w-full">
          <span>{`Transactions - ${programName}`}</span>
          {walletId && (
            <button
              onClick={handleRefreshClick}
              disabled={recalculatingTransactions}
              className="ml-4 p-2 text-gray-600 hover:text-brand hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Recalculate transactions"
            >
              <RefreshCw
                className={`w-5 h-5 ${recalculatingTransactions ? 'animate-spin' : ''}`}
              />
            </button>
          )}
        </div>
      }
      width="max-w-6xl w-[95vw]"
    >
      <div className="w-full flex flex-col min-h-0 flex-1 overflow-hidden max-h-[78vh]">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm flex items-center gap-2">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>Transactions recalculated successfully!</span>
          </div>
        )}

        {/* Error Message */}
        {recalculateTransactionsError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {recalculateTransactionsError}
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center py-12 border border-gray-100 rounded-lg">
            <Loader2 className="w-8 h-8 animate-spin text-brand/80" />
            <span className="ml-2 text-sm text-gray-500">Loading transactions...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && transactions && transactions.length === 0 && (
          <div className="text-center py-12 text-gray-500 border border-gray-100 rounded-lg">
            No transactions found for this program.
          </div>
        )}

        {!loading && !error && transactions && transactions.length > 0 && (
          <div className="border border-cardborder rounded-xl overflow-hidden flex flex-col h-full min-h-0">
            <div className="overflow-x-auto overflow-y-auto flex-1" style={{ maxHeight: '65vh' }}>
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Date
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Type
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Top Up
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Balance
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Bank
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-r border-gray-100">
                      Reference
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Remark
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => {
                    const transactionType = String(transaction.transactionTypeDisplay || transaction.transactionType || '').toLowerCase();
                    const isCredit = transactionType === 'credit' || transactionType === 'top up';
                    const date = isCredit
                      ? transaction.createdAt
                      : transaction.debitDate || transaction.settlementDate || transaction.createdAt;
                    const topUp = transaction.topUpDelta;
                    const balance = transaction.balanceDelta;
                    const bankName = (transaction as any)['banks.displayName'] || '-';

                    return (
                      <tr
                        key={transaction.transactionId}
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                        }`}
                      >
                        <td className="py-3 px-4 text-gray-700 border-r border-gray-100">
                          {formatDate(date)}
                        </td>
                        <td className="py-3 px-4 border-r border-gray-100">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isCredit
                                ? 'bg-green-100 text-green-800 border border-green-100'
                                : 'bg-red-100 text-red-800 border border-red-100'
                            }`}
                          >
                            {transaction.transactionTypeDisplay || transaction.transactionType || '-'}
                          </span>
                        </td>
                        <td
                          className="py-3 px-4 text-right font-medium border-r border-gray-100 text-gray-700"
                        >
                          {formatOptionalAmount(topUp)}
                        </td>
                        <td
                          className="py-3 px-4 text-right font-medium border-r border-gray-100 text-gray-700"
                        >
                          {formatOptionalAmount(balance)}
                        </td>
                        <td className="py-3 px-4 text-gray-600 border-r border-gray-100">
                          {bankName}
                        </td>
                        <td className="py-3 px-4 text-gray-600 border-r border-gray-100">
                          {transaction.transactionNumber || '-'}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {transaction.remark || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {transactions && transactions.length > 0 && (
              <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs text-gray-600">
                Total Transactions: {transactions.length}
              </div>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default DistributorTransactionModal;
